// DOCX Tab Node - Emulates Word's tab stop behavior in TipTap/ProseMirror.
//
// PERFORMANCE OPTIMIZATIONS:
// 1. Skip initial load - Backend pre-calculates tab widths (materializeTabWidths.ts)
// 2. Node equality check - Skip paragraphs that didn't change (content OR attrs like indent/alignment)
// 3. Single transaction dispatch - Batch all PM updates to avoid transaction storm
// 4. RAF scheduling - Coalesce multiple dirty blocks into single layout pass
// 5. PM model check before DOM - Avoid DOM access for paragraphs without tabs

import { Node, mergeAttributes } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';

import { ETiptapSelectorKey } from '@modules/documents/types/tiptap.types';
import { DOCX_TAB_FLUSH, TAB_NODE_NAME } from './constants';
import getTabLayoutPlugin from './getTabLayoutPlugin';
import { markSystemTr } from '../../plugins/shared';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [TAB_NODE_NAME]: {
      insertTab: () => ReturnType;
      flushTabLayout: () => ReturnType;
    };
  }
}

const TabNode = Node.create<{ isEditMode: (() => boolean) | null }>({
  name: TAB_NODE_NAME,
  inline: true,
  group: 'inline',
  atom: true,
  selectable: false,
  draggable: false,

  addOptions() {
    return { isEditMode: null };
  },

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute('style'),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span.${TAB_NODE_NAME}` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ class: TAB_NODE_NAME }, HTMLAttributes)];
  },

  addCommands() {
    return {
      flushTabLayout:
        () =>
        ({ tr }) => {
          markSystemTr(tr).setMeta(DOCX_TAB_FLUSH, true);
          return true;
        },
      insertTab:
        () =>
        ({ chain }) =>
          chain().insertContent({ type: this.name }).run(),
    };
  },

  addKeyboardShortcuts() {
    // taskList uses taskItem, not listItem - both count as a list item to nest.
    const listItemTypeAtStart = (state: EditorState): string | null => {
      const {
        selection: { $from },
      } = state;
      if ($from.parent.type.name !== ETiptapSelectorKey.PARAGRAPH) return null;
      if ($from.parentOffset !== 0) return null; // caret at absolute start
      for (let d = $from.depth; d > 0; d--) {
        const name = $from.node(d).type.name;
        if (name === ETiptapSelectorKey.LIST_ITEM || name === ETiptapSelectorKey.TASK_ITEM) return name;
      }
      return null;
    };

    return {
      // Alt-Tab (macOS): insert a tab inside table cells (Word parity).
      'Alt-Tab': ({ editor }) => {
        if (editor.isActive(ETiptapSelectorKey.TABLE)) return editor.commands.insertTab();
        return false;
      },

      Tab: ({ editor }) => {
        // Tables keep default nav (unless Alt-Tab above).
        if (editor.isActive(ETiptapSelectorKey.TABLE)) return false;

        // Headings allow only text, so insertContent would split them to place the tab node.
        if (editor.isActive(ETiptapSelectorKey.HEADING)) return true;

        // Lists: nest only at item start; otherwise insert a tab span.
        if (
          editor.isActive(ETiptapSelectorKey.BULLET_LIST) ||
          editor.isActive(ETiptapSelectorKey.ORDERED_LIST_WITH_START) ||
          editor.isActive(ETiptapSelectorKey.TASK_LIST)
        ) {
          const itemType = listItemTypeAtStart(editor.state);
          if (itemType && editor.can().sinkListItem(itemType)) return false;
          return editor.commands.insertTab();
        }

        // Everywhere else
        return editor.commands.insertTab();
      },
    };
  },

  addProseMirrorPlugins() {
    // The document session stays in Editing mode during temporary interaction locks (AI review).
    return [getTabLayoutPlugin(() => this.options.isEditMode?.() ?? this.editor.isEditable)];
  },
});

export default TabNode;
